import { useQuery } from "@tanstack/react-query";
import { useMatch } from "react-router-dom";
import { useServices } from "@/services/ServiceProvider";
import { useAuthStore } from "@/stores/useAuthStore";
import { workspaceMembersKey } from "@/lib/userQueryKeys";
import { ROUTES } from "@/lib/constants";
import type { WorkspaceMember } from "@/lib/types";

// ─── Workspace context hook ─────────────────────────────

/**
 * Returns workspace context for the currently active workspace.
 * When `activeQuestId` is null, we're in solo/conversation mode.
 * When set, we're in workspace mode with shared data from Supabase.
 *
 * Which conversation is open is read from the URL, never from a store: a
 * store is empty after a reload, so the chat would silently reopen in solo
 * mode and the conversation could not be linked or bookmarked.
 *
 * `useMatch`, not `useParams` — SessionList calls this from AppShell's
 * sidebar, which renders OUTSIDE the matched <Route>, and `useParams()`
 * returns {} there.
 */
export function useWorkspaceContext() {
  const match = useMatch(ROUTES.ASSISTANT_WORKSPACE);
  const activeQuestId = match?.params.questId ?? null;
  const userId = useAuthStore((s) => s.session?.userId);
  const { workspace } = useServices();

  const isWorkspace = !!activeQuestId;

  // Fetch workspace members when in workspace mode — poll for new joins
  const {
    data: members = [],
    isPending,
    isFetching,
    isError,
  } = useQuery({
    queryKey: workspaceMembersKey(activeQuestId, userId),
    queryFn: () => workspace.getWorkspaceMembers(activeQuestId!),
    enabled: isWorkspace,
    staleTime: 5_000,
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });

  // Determine the current user's role in this workspace
  const currentMember = members.find((m: WorkspaceMember) => m.userId === userId);
  const userRole = currentMember?.role ?? null;

  // Membership in THIS quest, which is what a caller gating on access needs.
  // Deliberately not derived from the `my-workspaces` list: that list is
  // scoped to the active chain (`quests.chain_id`), so a genuine member
  // viewing with the other network selected is absent from it and reads as a
  // stranger. This row comes from `workspace_members` by quest id alone, and
  // RLS (`is_workspace_member`) already returns nothing to an outsider — so
  // an empty result IS the denial, on any network.
  //
  // `membershipSettled` is the honest form of "the answer is in": a query
  // that is loading, refetching a stale list, or errored has not answered,
  // and treating any of those as "not a member" evicts real members.
  const membershipSettled = isWorkspace && !isPending && !isFetching && !isError;

  // `roleResolved` is the weaker "we have an answer to SHOW". A background
  // refetch — every 10s, from the poll above — keeps the previous rows, and
  // those rows are a real reading, not a loading default. Display that keys
  // off `membershipSettled` blinks out for the length of every poll (the
  // workspace budget line read "Volunteer", then nothing, then "Volunteer").
  // Gate DISPLAY on this; gate ACCESS decisions on `membershipSettled`.
  const roleResolved = isWorkspace && !isPending && !isError;

  return {
    isWorkspace,
    activeQuestId,
    members,
    userRole,
    currentMember,
    isMember: !!currentMember,
    membershipSettled,
    roleResolved,
  };
}
